import React from 'react';
import {List} from 'immutable';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  Atom,
  Plugins,
  withSystem,
  withMeta,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {
  TListOfElement,
  TText,
  TTypeConstructor,
  TRecord,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {createCurrentVariationDataAttribute} from '@packages/systems/optimization/utils';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getRecordOrElse,
  getStringOrElse,
  getTagOrElse,
} from '@packages/utilities/data-utils';

/**
 * Exported separately from `Atom.createRender` so tests can render it as a
 * regular React component without spinning up the full WFDL render harness.
 * `Atom.createRender` is a brand wrapper — it returns the component unchanged.
 */
export const OptimizationBaseRender = ({
  context,
  data,
  attributes,
  classes,
  generateAttributes,
  renderChild,
  elementId,
}: AtomRenderProps) => {
  const runtimeEnvironment = useRuntimeEnvironment();
  const isSSR = runtimeEnvironment === WebflowEnvironments.SSR;
  const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
  const experiencesByVariationId = getRecordOrElse(
    getInPojo(data, ['experiencesByVariationId']),
    {}
  );

  let updatedAttributes = attributes;
  let updatedClasses = classes.push('w-optimization');

  if (isSSR) {
    Object.entries(experiencesByVariationId).forEach(
      ([variationId, experienceId]) => {
        updatedAttributes = updatedAttributes.set(
          `data-wf-experience-${getStringOrElse(experienceId, '')}`,
          ''
        );
        updatedAttributes = updatedAttributes.set(
          `data-wf-variation-${variationId}`,
          ''
        );
      }
    );
  } else {
    const currentVariationId = getStringOrElse(
      getInPojo(data, ['temp', 'currentVariationId']),
      ''
    );
    const matchingVariationAtomId = getStringOrElse(
      getInPojo(data, ['temp', 'matchingVariationAtomId']),
      ''
    );

    if (matchingVariationAtomId && matchingVariationAtomId !== elementId) {
      updatedClasses = List(['w-hidden']);
    }

    if (Object.hasOwn(experiencesByVariationId, currentVariationId)) {
      updatedAttributes = updatedAttributes.set(
        createCurrentVariationDataAttribute(currentVariationId),
        ''
      );
    }
  }

  return (
    <Tag {...generateAttributes(updatedAttributes, updatedClasses)}>
      {getChildren(data).map((child: EvaluatedNodeData) =>
        renderChild(child, context)
      )}
    </Tag>
  );
};

export default createIntegratedAtom({
  displayName: 'Base',
  icon: {
    type: 'Component',
    value: qualify(
      Plugins.name('Optimization'),
      iconName('OptimizationVariation')
    ),
  },
  render: Atom.createRender(OptimizationBaseRender),
  showEmpty: true,
  constraints: {
    pinToParent: true,
    parent: [
      {
        is: 'OptimizationExperience',
        name: 'Optimization',
        rule: 'ExactlyOne',
      },
    ],
    children: [
      {
        is: {
          name: 'element',
          check: () => true,
        },
        rule: 'ZeroOrOne',
      },
    ],
    descendants: [
      {
        is: 'DynamoWrapper',
        rule: 'Forbid',
      },
    ],
  },
  fields: {
    tag: withMeta(
      {variant: 'menu'},
      withSystem(
        {
          defaultValue: 'div',
          label: 'Tag',
          private: true,
        },
        TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
      )
    ),
    children: TListOfElement,
    temp: withSystem(
      {isBindable: false, defaultValue: {}, private: true},
      TRecord({
        currentVariationId: withSystem(
          {isBindable: false, defaultValue: '', private: true},
          TText
        ),
        matchingVariationAtomId: withSystem(
          {isBindable: false, defaultValue: '', private: true},
          TText
        ),
      })
    ),
    experiencesByVariationId: withSystem(
      {isBindable: false, defaultValue: {}, private: true},
      TRecord({}, TTypeReference('extraExperiencesByVariationId'))
    ),
  },
});
